import { useCallback, useEffect, useRef, useState } from "react";
import { useConvex } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useGameStore } from "@/store/gameStore";
import { gameDocToGame } from "@/hooks/convex/gameMapping";

export type JoinStatus =
  "loading" | "joined" | "not-found" | "offline" | "error";

const JOIN_TIMEOUT_MS = 8000;

export const normalizeJoinSlug = (slug: string | undefined) =>
  slug?.trim().toUpperCase() ?? "";

export function useConvexJoin(slug: string | undefined) {
  const convex = useConvex();
  const setGame = useGameStore((state) => state.setGame);
  const [status, setStatus] = useState<JoinStatus>("loading");
  const [attempt, setAttempt] = useState(0);
  const requestId = useRef(0);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    const handleOnline = () => retry();
    const handleOffline = () => setStatus("offline");
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [retry]);

  useEffect(() => {
    const normalizedSlug = normalizeJoinSlug(slug);
    const currentRequest = ++requestId.current;

    if (!normalizedSlug) {
      setStatus("not-found");
      return;
    }
    if (!navigator.onLine) {
      setStatus("offline");
      return;
    }

    setStatus("loading");
    let active = true;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeout = setTimeout(
        () => reject(new Error("Join request timed out")),
        JOIN_TIMEOUT_MS,
      );
    });

    const loadProtectedGame = async () => {
      const game = await convex.query(api.game.get, { slug: normalizedSlug });
      if (!game?.cards?.length || !game.courts?.length) return null;
      return game.roundId
        ? game
        : await convex.mutation(api.game.ensureRound, { slug: normalizedSlug });
    };

    Promise.race([loadProtectedGame(), timeoutPromise])
      .then((protectedGame) => {
        if (!active || requestId.current !== currentRequest) return;
        if (!protectedGame) {
          setStatus("not-found");
          return;
        }
        if (!protectedGame.roundId) {
          setStatus("error");
          return;
        }

        setGame(gameDocToGame(protectedGame), { clearLocalHistory: true });
        setStatus("joined");
      })
      .catch((error: unknown) => {
        if (!active || requestId.current !== currentRequest) return;
        console.error("Join game error:", error);
        setStatus(navigator.onLine ? "error" : "offline");
      })
      .finally(() => {
        if (timeout) clearTimeout(timeout);
      });

    return () => {
      active = false;
      if (timeout) clearTimeout(timeout);
    };
  }, [attempt, convex, setGame, slug]);

  return { status, retry };
}
